import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Download, RefreshCw, Trash2 } from 'lucide-react';
import ReportHeader from '@/components/ReportHeader';
import { useFilialSelecionada } from '@/hooks/useFilialSelecionada';
import { apiService } from '@/services/api';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LabelList } from 'recharts';

const Container = styled.div`
  padding: 16px;
`;

const Filtros = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const Campo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const GraficoBox = styled.div`
  width: 100%;
  height: 340px;
  padding: 8px 12px 16px 12px;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const Acoes = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
`;

const Botao = styled.button`
  height: 36px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #111827;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px;
  border-bottom: 1px solid #f1f5f9;
`;

const IconButton = styled.button`
  border: none;
  background: transparent;
  padding: 4px;
  cursor: pointer;
  color: #9ca3af;
  &:hover { color: #ef4444; }
`;

const RelatorioMediaVendasVendedor: React.FC = () => {
  const { filialSelecionada } = useFilialSelecionada();
  const [dataInicio, setDataInicio] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [dataFim, setDataFim] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [todasFiliais, setTodasFiliais] = useState<boolean>(true);
  const [excluidos, setExcluidos] = useState<Set<string>>(() => new Set());
  const [mostrarMedia, setMostrarMedia] = useState<boolean>(true);
  const [mostrarTotal, setMostrarTotal] = useState<boolean>(true);

  const queryKey = useMemo(() => (
    ['relatorio-media-vendedor', todasFiliais ? 'todas' : (filialSelecionada?.id || '-'), dataInicio, dataFim]
  ), [todasFiliais, filialSelecionada?.id, dataInicio, dataFim]);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey,
    enabled: todasFiliais || !!filialSelecionada?.id,
    queryFn: async () => {
      if (todasFiliais) {
        const res = await apiService.obterRelatorioMediaVendasVendedorTodas({ dataInicio, dataFim });
        return res?.data;
      }
      if (!filialSelecionada?.id) return null;
      const res = await apiService.obterRelatorioMediaVendasVendedor(filialSelecionada.id, { dataInicio, dataFim });
      return res?.data;
    }
  });

  const linhas: any[] = data?.dados || [];

  const keyLinha = (linha: any): string => {
    return `${String(linha?.Filial ?? '')}|${String(linha?.Nome ?? '')}`;
  };

  const linhasVisiveis = useMemo(() => {
    if (!Array.isArray(linhas)) return [];
    if (!excluidos || excluidos.size === 0) return linhas;
    return linhas.filter(l => !excluidos.has(keyLinha(l)));
  }, [linhas, excluidos]);
  const colunas: string[] = useMemo(() => {
    const base = (linhas.length > 0 ? Object.keys(linhas[0]) : [
      'Filial','Nome','qtdeVendas','qtdeVendasGeral','ValorTotal','MediaParcelas','MediaFormulaRodrigo',
      'rec12','rec11','rec10','rec9','rec8','rec7','rec6','rec5','rec4','rec3','rec2','rec1','ValorTotalGeral'
    ]);
    // Remove 'MediaParcelas' apenas se também existir 'MediaFormulaRodrigo' (compatibilidade retroativa)
    const hasMFR = base.includes('MediaFormulaRodrigo');
    const arr = base.filter(c => !(c === 'MediaParcelas' && hasMFR));
    // Garante 'Filial' como primeira coluna quando presente
    const idxFilial = arr.indexOf('Filial');
    if (idxFilial > 0) {
      arr.splice(idxFilial, 1);
      arr.unshift('Filial');
    }
    // Reordenar para colocar 'ValorTotalGeral' imediatamente após 'ValorTotal'
    const idxGeral = arr.indexOf('ValorTotalGeral');
    const idxCredito = arr.indexOf('ValorTotal');
    if (idxGeral !== -1 && idxCredito !== -1 && idxGeral !== idxCredito + 1) {
      arr.splice(idxGeral, 1);
      arr.splice(idxCredito + 1, 0, 'ValorTotalGeral');
    }
    return arr;
  }, [linhas]);

  const headerLabels = useMemo<Record<string, string>>(() => ({
    Filial: 'Filial',
    Nome: 'Vendedor',
    qtdeVendas: 'Qtde Vendas Crédito',
    qtdeVendasGeral: 'Qtde Vendas Total',
    ValorTotal: 'Valor Crédito',
    MediaParcelas: 'Média Parcelas',
    MediaFormulaRodrigo: 'Média Parcelas',
    rec12: '12X',
    rec11: '11X',
    rec10: '10X',
    rec9: '9X',
    rec8: '8X',
    rec7: '7X',
    rec6: '6X',
    rec5: '5X',
    rec4: '4X',
    rec3: '3X',
    rec2: '2X',
    rec1: '1X',
    ValorTotalGeral: 'Valor Total'
  }), []);

  const format2 = (v: any) => {
    const n = Number(v) || 0;
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const colunasNumericas = useMemo(() => new Set([
    'ValorTotal','MediaParcelas','MediaFormulaRodrigo','ValorTotalGeral',
    'rec12','rec11','rec10','rec9','rec8','rec7','rec6','rec5','rec4','rec3','rec2','rec1'
  ]), []);

  const handleExportar = () => {
    const rows = linhasVisiveis || [];
    const cols = colunas || [];
    if (rows.length === 0 || cols.length === 0) return;
    const sep = ';';

    const toCsvValue = (coluna: string, valor: any): string => {
      const raw = colunasNumericas.has(coluna)
        ? format2(valor)
        : String(valor ?? '');
      const needsQuote = raw.includes('"') || raw.includes(sep) || /[\n\r]/.test(raw);
      const escaped = raw.replace(/"/g, '""');
      return needsQuote ? `"${escaped}"` : escaped;
    };

    const header = cols.map((c) => headerLabels[c] ?? c).join(sep);
    const body = rows.map((linha) => cols.map((c) => toCsvValue(c, linha[c])).join(sep)).join('\n');

    const prefixo = 'relatorio_media_vendas_vendedor';
    const contexto = todasFiliais ? 'todas' : (filialSelecionada?.nome?.replace(/[^\w\-\s]/g, '') || 'filial');
    const nomeArquivo = `${prefixo}_${contexto}_${dataInicio}_a_${dataFim}.csv`;

    const csvContent = `\ufeff${header}\n${body}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeArquivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const graficoData = useMemo(() => {
    const rows = Array.isArray(linhasVisiveis) ? linhasVisiveis : [];
    const arr = rows.map((l: any) => ({
      nome: String(l.Nome ?? ''),
      media: Number(l.MediaParcelas ?? l.MediaFormulaRodrigo ?? 0),
      total: Number(l.ValorTotal ?? 0)
    }));
    arr.sort((a, b) => (a.media || 0) - (b.media || 0));
    return arr;
  }, [linhasVisiveis]);

  return (
    <Container>
      <ReportHeader
        title="Relatório Média de Vendas por Vendedor"
        subtitle={`${filialSelecionada?.nome || ''} • ${data?.totalRegistros ?? 0} registros`}
        rightActions={(
          <Acoes>
            <Botao onClick={handleExportar} disabled={isLoading || isFetching || linhasVisiveis.length === 0}>
              <Download size={16} /> Exportar
            </Botao>
            <Botao onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw size={16} /> Atualizar
            </Botao>
          </Acoes>
        )}
      />

      <Filtros>
        <Campo>
          <Label>Data Início</Label>
          <Input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
        </Campo>
        <Campo>
          <Label>Data Fim</Label>
          <Input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
        </Campo>
        <Campo>
          <Label>&nbsp;</Label>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={todasFiliais}
              onChange={(e) => setTodasFiliais(e.target.checked)}
            />
            Todas as Filiais
          </CheckboxRow>
        </Campo>
        <Campo>
          <Label>Séries</Label>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={mostrarMedia}
              onChange={(e) => setMostrarMedia(e.target.checked)}
            />
            Média de Vendas
          </CheckboxRow>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={mostrarTotal}
              onChange={(e) => setMostrarTotal(e.target.checked)}
            />
            Total de Vendas
          </CheckboxRow>
        </Campo>
        <Acoes>
          <Botao onClick={() => refetch()} disabled={isLoading || isFetching}>
            <Calendar size={16} /> Aplicar
          </Botao>
        </Acoes>
      </Filtros>

      <GraficoBox>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={graficoData} margin={{ top: 10, right: 28, bottom: 72, left: 0 }} barGap={6}>
            <XAxis dataKey="nome" tick={false} axisLine={false} />
            <YAxis
              yAxisId="left"
              tickFormatter={(v) => Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={(v) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}
            />
            <Tooltip
              formatter={(v: number, _n: string, p: any) => {
                if (p?.dataKey === 'total') {
                  return [Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), 'Valor Total'];
                }
                return [format2(v), 'Média'];
              }}
              labelFormatter={(label) => String(label)}
            />
            {mostrarMedia && (
              <Bar dataKey="media" yAxisId="left" fill="#3b82f6" radius={[6,6,0,0]}>
                <LabelList dataKey="media" position="top" formatter={(v: number) => format2(v)} />
                {/* Nomes abaixo das barras: usar na série de média quando presente */}
                <LabelList dataKey="nome" position="bottom" offset={10} fill="#3b82f6" />
              </Bar>
            )}
            {mostrarTotal && (
              <Bar dataKey="total" yAxisId="right" fill="#10b981" radius={[6,6,0,0]}>
                {/* Se a série de média estiver oculta, assumimos os nomes na série de total */}
                {!mostrarMedia && <LabelList dataKey="nome" position="bottom" offset={10} fill="#10b981" />}
              </Bar>
            )}
          </BarChart>
        </ResponsiveContainer>
      </GraficoBox>

      <div style={{ overflowX: 'auto' }}>
        <Tabela>
          <thead>
            <tr>
              <Th style={{ width: 36 }}></Th>
              {colunas.map((c) => (
                <Th key={c}>{headerLabels[c] ?? c}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><Td colSpan={colunas.length + 1}>Carregando...</Td></tr>
            ) : linhasVisiveis.length === 0 ? (
              <tr><Td colSpan={colunas.length + 1}>Nenhum registro encontrado</Td></tr>
            ) : (
              linhasVisiveis.map((linha, idx) => (
                <tr key={idx}>
                  <Td>
                    <IconButton
                      title="Remover visualmente"
                      onClick={() => {
                        setExcluidos(prev => {
                          const nxt = new Set(prev);
                          nxt.add(keyLinha(linha));
                          return nxt;
                        });
                      }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </Td>
                  {colunas.map((c) => (
                    <Td key={c}>
                      {['ValorTotal','MediaParcelas','MediaFormulaRodrigo','ValorTotalGeral','rec12','rec11','rec10','rec9','rec8','rec7','rec6','rec5','rec4','rec3','rec2','rec1'].includes(c)
                        ? format2(linha[c])
                        : String(linha[c] ?? '')}
                    </Td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Tabela>
      </div>
    </Container>
  );
};

export default RelatorioMediaVendasVendedor;


